import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  ArrowLeft, HardHat, DollarSign, Award, Building2, Loader2,
  ShoppingCart, CreditCard, Wallet, TrendingDown, Clock, X,
  PlusCircle, FileText, Trash2, Link2Off, History as HistoryIcon,
  Send, AlertTriangle, CheckCircle2, Hourglass, Tag, Pencil, Layers, Save,
  ListChecks, HandCoins, FileImage, FolderOpen, ClipboardList, Warehouse, Boxes, ListTodo, Eye
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
// Fix BN (2026-09-08): formatCantidad unifica las cantidades de material en 2 decimales.
import { formatPYG, formatDate, formatCantidad } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import CertificacionesTab from "./CertificacionesTab";
// Fix BQ (2026-09-08): FiltroMulti es el mismo desplegable de multiselección del
// Fix BM, reusado acá para no tener dos comportamientos distintos de filtro.
import TareasTab, { FiltroMulti } from "./TareasTab";
import ComprasImputacionTab from "./ComprasImputacionTab";   // Fix CN (2026-09-13)
import TareasPanel from "../../components/TareasPanel";   // Fix CM (2026-09-13)
import PagosTab from "./PagosTab";
import InformesTab from "./InformesTab";
import ArchivosTab from "./ArchivosTab";
import PresupuestoTab from "./PresupuestoTab";
import ChipsOrigen from "./ChipsOrigen";   // Fix EC (2026-09-24)
// Fix V (2026-09-02): pestaña "Actualización 2.0" (estado de los seis pasos por obra)
import Activacion20Tab from "./Activacion20Tab";

const opts = { withCredentials: true };
const MANAGE_ROLES = ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica"];
const RESIDENTE_ROLE = "Residente de Obra";
// CEN 2.0 FASE 4 — Coordinador de Obra suma acceso completo (montos) en Tareas/Pagos/Presupuesto.
const FULL_ACCESS_ROLES = [...MANAGE_ROLES, "Coordinador de Obra"];
// CEN 2.0 FASE 3 PARTE A — edición de marcas cotizadas desde el módulo Obra (coordinador)
const MARCAS_EDIT_ROLES = ["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Coordinador de Obra"];
const CONSUMO_ESTADO = {
  excedido: { label: "Excedido", cls: "bg-red-100 text-red-700 border-red-200" },
  por_agotarse: { label: "Por agotarse", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  ok: { label: "OK", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const STATUS_BADGE = {
  activa:     { label: "Activa",     cls: "bg-emerald-100 text-emerald-700" },
  pausada:    { label: "Pausada",    cls: "bg-amber-100 text-amber-700" },
  finalizada: { label: "Finalizada", cls: "bg-blue-100 text-blue-700" },
  cancelada:  { label: "Cancelada",  cls: "bg-red-100 text-red-700" },
};

// Devuelve {key, label, badgeCls} del origen de un ítem según el orden de anexado.
function origenInfo(item, adicionalesIds) {
  if (item.origen_tipo === "adicional") {
    const idx = (adicionalesIds || []).indexOf(item.origen_budget_id);
    const n = idx >= 0 ? idx + 1 : "?";
    return { key: item.origen_budget_id, label: `Adicional ${n}`, badgeCls: "bg-orange-100 text-orange-700" };
  }
  return { key: "base", label: "Base", badgeCls: "bg-emerald-100 text-emerald-700" };
}

// ─── EJECUCIÓN EN CAMPO ─────────────────────────────────────────────────────
function EjecucionTab({ obraId, adicionalesIds, isResidente }) {
  const [items, setItems] = useState([]);
  const [ponderado, setPonderado] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingIdx, setSavingIdx] = useState(null);
  const [filter, setFilter] = useState("todos");
  // Fix BP (2026-09-08): "esa vista se puede agregar en otra hoja nomas acá"
  // (Edson, 08/09). Dos hojas sobre los mismos datos: la de siempre y la matriz.
  const [vista, setVista] = useState("items");
  // % operativo consolidado por ítem, para contrastarlo contra el % certificado.
  const [operativo, setOperativo] = useState({});
  // Fix BQ (2026-09-08): "agregar filtro multiselección en esta ventana"
  // (Edson, 08/09). Conjunto vacío = sin filtrar, igual que en la pestaña Tareas.
  const [filtroSecciones, setFiltroSecciones] = useState(new Set());
  const [filtroAvance, setFiltroAvance] = useState(new Set());
  const [miEnvio, setMiEnvio] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [obsEnvio, setObsEnvio] = useState("");
  const savedRef = useRef({});

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/obras/${obraId}/ejecucion`, opts)
      .then(({ data }) => {
        const its = data.items || [];
        setItems(its);
        setPonderado(data.avance_ponderado || 0);
        const snap = {};
        its.forEach((it, i) => { snap[i] = { pct: Number(it.avance_real_pct) || 0, obs: it.avance_real_observaciones || "" }; });
        savedRef.current = snap;
      })
      .catch(() => toast.error("Error cargando ejecución"))
      .finally(() => setLoading(false));
  }, [obraId]);
  useEffect(load, [load]);

  // Fix BP (2026-09-08): el consolidado por ítem sale de las tareas, así que se
  // pide aparte. Si la obra no tiene tareas todavía, queda vacío y la columna
  // muestra un guión: no rompe nada.
  const loadOperativo = useCallback(() => {
    axios.get(`${API}/obras/${obraId}/avance-operativo`, opts)
      .then(({ data }) => setOperativo(data.por_item || {}))
      .catch(() => setOperativo({}));
  }, [obraId]);
  useEffect(loadOperativo, [loadOperativo]);

  const loadMiEnvio = useCallback(() => {
    if (!isResidente) return;
    axios.get(`${API}/obras/${obraId}/mi-ultimo-envio`, opts)
      .then(({ data }) => setMiEnvio(data.envio || null))
      .catch(() => {});
  }, [obraId, isResidente]);
  useEffect(loadMiEnvio, [loadMiEnvio]);

  const isPendiente = miEnvio?.estado === "pendiente_aprobacion";
  const locked = isResidente && isPendiente;

  const setField = (idx, field, value) => {
    if (locked) return;
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const save = async (idx) => {
    if (isResidente) return; // El Residente envía cambios en bloque vía certificación, no auto-guarda.
    const it = items[idx];
    const pct = Math.max(0, Math.min(100, Number(it.avance_real_pct) || 0));
    const obs = it.avance_real_observaciones || "";
    const prev = savedRef.current[idx] || { pct: 0, obs: "" };
    if (prev.pct === pct && prev.obs === obs) return;
    setSavingIdx(idx);
    try {
      const { data } = await axios.post(`${API}/obras/${obraId}/ejecucion/avance`, {
        item_idx: idx, avance_real_pct: pct, observaciones: obs,
      }, opts);
      setPonderado(data.avance_ponderado);
      savedRef.current[idx] = { pct: data.avance_real_pct, obs };
      setItems(prevItems => prevItems.map((x, i) => i === idx ? { ...x, avance_real_pct: data.avance_real_pct, avance_real_updated_at: new Date().toISOString(), avance_real_updated_by_name: "tú" } : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar avance");
      load();
    } finally { setSavingIdx(null); }
  };

  const enviarCertificacion = async () => {
    const changed = items
      .map((it, idx) => ({ idx, it }))
      .filter(({ idx, it }) => Math.abs((Number(it.avance_real_pct) || 0) - (savedRef.current[idx]?.pct || 0)) >= 0.01 && !it.is_group);
    if (changed.length === 0) { toast.error("No hay cambios de % de avance para enviar"); return; }
    setEnviando(true);
    try {
      await axios.post(`${API}/obras/${obraId}/ejecucion/enviar-certificacion`, {
        items: changed.map(({ idx, it }) => ({ item_idx: idx, avance_propuesto_pct: Math.max(0, Math.min(100, Number(it.avance_real_pct) || 0)) })),
        observaciones: obsEnvio,
      }, opts);
      toast.success("Avance enviado para aprobación");
      setObsEnvio("");
      loadMiEnvio();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Error al enviar avance");
    } finally { setEnviando(false); }
  };

  if (loading) return <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>;

  // Filtros: Todos / Base / Adicional N
  const filterOptions = [["todos", "Todos"], ["base", "Base"]];
  (adicionalesIds || []).forEach((bid, i) => filterOptions.push([bid, `Adicional ${i + 1}`]));

  const matchesFilter = (it) => {
    if (filter === "todos") return true;
    if (filter === "base") return it.origen_tipo !== "adicional";
    return it.origen_budget_id === filter;
  };

  // Fix BQ (2026-09-08): sección de cada fila = el último encabezado azul que
  // apareció antes. Son constantes comunes y no useMemo a propósito: acá arriba ya
  // hubo un return temprano por `loading`, y un hook después de un return
  // condicional rompe el orden de hooks de React.
  const seccionPorIdx = [];
  let _seccionActual = "(sin sección)";
  items.forEach((it) => {
    if (it.is_group) _seccionActual = `${it.numero || ""} ${it.descripcion || ""}`.trim() || "(sin sección)";
    seccionPorIdx.push(_seccionActual);
  });

  const opcionesSeccion = [...new Set(seccionPorIdx)].map(s => ({ value: s, label: s }));
  const opcionesAvance = [
    { value: "sin", label: "Sin iniciar (0%)" },
    { value: "proceso", label: "En proceso (1-99%)" },
    { value: "completo", label: "Completo (100%)" },
  ];
  const tramoAvance = (it) => {
    const p = Number(it.avance_real_pct) || 0;
    return p >= 100 ? "completo" : p > 0 ? "proceso" : "sin";
  };

  // Un encabezado de sección se muestra solo si le queda algún ítem visible.
  const idxVisibles = new Set();
  const _grupoTieneHijos = {};
  let _grupoActual = -1;
  items.forEach((it, i) => {
    if (it.is_group) { _grupoActual = i; _grupoTieneHijos[i] = false; return; }
    const pasa = matchesFilter(it)
      && (filtroSecciones.size === 0 || filtroSecciones.has(seccionPorIdx[i]))
      && (filtroAvance.size === 0 || filtroAvance.has(tramoAvance(it)));
    if (pasa) {
      idxVisibles.add(i);
      if (_grupoActual >= 0) _grupoTieneHijos[_grupoActual] = true;
    }
  });
  Object.entries(_grupoTieneHijos).forEach(([i, tiene]) => { if (tiene) idxVisibles.add(Number(i)); });
  const nVisibles = items.filter((it, i) => !it.is_group && idxVisibles.has(i)).length;
  const nItems = items.filter(it => !it.is_group).length;

  // Subtotales por origen (Σ monto de ítems no-grupo)
  const subtotales = {};
  items.forEach(it => {
    if (it.is_group) return;
    const info = origenInfo(it, adicionalesIds);
    subtotales[info.label] = (subtotales[info.label] || 0) + Number(it.monto || 0);
  });
  const granTotal = Object.values(subtotales).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4" data-testid="obra-ejecucion-tab">
      {/* Banner de mi envío (Residente) */}
      {isResidente && miEnvio && (
        <div
          className={`flex items-start gap-3 rounded-2xl border p-4 ${isPendiente ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`}
          data-testid="obra-mi-envio-banner"
        >
          <div className={`p-2 rounded-xl flex-shrink-0 ${isPendiente ? "bg-amber-100 text-amber-600" : "bg-red-100 text-red-600"}`}>
            {isPendiente ? <Hourglass size={18} /> : <AlertTriangle size={18} />}
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-bold ${isPendiente ? "text-amber-800" : "text-red-800"}`}>
              {isPendiente ? "Tu envío está pendiente de aprobación" : "Tu último envío fue rechazado"}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {isPendiente
                ? "El Gerente Técnico o Administrador debe revisar tus % propuestos antes de que se reflejen en la obra."
                : (<>Motivo: <span className="font-medium text-gray-700">{miEnvio.comentario_rechazo || "Sin comentario"}</span> — corregí y volvé a enviar.</>)}
            </p>
          </div>
        </div>
      )}

      {/* Fix BP (2026-09-08): selector de hoja — Ítems (la de siempre) o Matriz. */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit" data-testid="obra-ejecucion-vista">
        {[["items", "Ítems"], ["matriz", "Matriz de avance"]].map(([key, label]) => (
          <button key={key} onClick={() => setVista(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${vista === key ? "bg-white text-[#EA580C] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            data-testid={`obra-ejecucion-vista-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {vista === "matriz" && <MatrizAvanceCard obraId={obraId} onCambio={loadOperativo} />}

      {/* Filtros por origen */}
      {/* Fix BQ (2026-09-08): los dos filtros de multiselección, con el mismo
          componente y el mismo comportamiento que los de la pestaña Tareas. */}
      <div className={`flex gap-2 flex-wrap items-center ${vista === "items" ? "" : "hidden"}`} data-testid="obra-ejecucion-filtros">
        <FiltroMulti etiqueta="Secciones" opciones={opcionesSeccion} seleccion={filtroSecciones}
          onAplicar={setFiltroSecciones} testId="ejecucion-filtro-seccion" />
        <FiltroMulti etiqueta="Avance" opciones={opcionesAvance} seleccion={filtroAvance}
          onAplicar={setFiltroAvance} testId="ejecucion-filtro-avance" />
        {(filtroSecciones.size > 0 || filtroAvance.size > 0) && (
          <button type="button" onClick={() => { setFiltroSecciones(new Set()); setFiltroAvance(new Set()); }}
            className="text-xs font-semibold text-gray-500 hover:text-gray-700 underline"
            data-testid="ejecucion-limpiar-filtros">
            Limpiar filtros
          </button>
        )}
        <span className="text-xs font-semibold text-gray-500" data-testid="ejecucion-conteo">
          Mostrando {nVisibles} de {nItems}
        </span>
      </div>

      <div className={`flex gap-2 flex-wrap ${vista === "items" ? "" : "hidden"}`} data-testid="obra-origen-filtros">
        {filterOptions.map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${filter === key ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white text-gray-600 border-gray-300 hover:border-[#EA580C]/40"}`} data-testid={`obra-filtro-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {vista === "items" && (
      /* Fix BQ (2026-09-08): "que queden fijos los encabezados como hiciste en el
         apartado de pedido de materiales" (Edson, 08/09). Mismo patrón del Fix BB:
         la tabla tiene altura acotada y scroll propio, y el `sticky` va en cada
         celda del encabezado (variante [&_th]) y no solo en el thead, que es lo
         que lo hace funcionar parejo en todos los navegadores. */
      <div className="overflow-auto max-h-[70vh] rounded-2xl border border-gray-200/60 bg-white relative" data-testid="obra-ejecucion-scroll">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-20 [&_th]:sticky [&_th]:top-0 [&_th]:z-20 [&_th]:bg-gray-50 [&_th]:shadow-[inset_0_-1px_0_rgb(243,244,246)]">
            <tr className="bg-gray-50 border-b border-gray-100 text-left">
              <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs w-16">N°</th>
              {/* Fix EU (2026-10-01): en el celular la descripción no se aprieta a una palabra por renglón */}
              <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs max-lg:min-w-[220px]">Descripción</th>
              <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs w-20">Unidad</th>
              <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs w-24 text-right">Cantidad</th>
              {/* Fix BP (2026-09-08): el operativo viene de las tareas y es de solo
                  lectura acá; el % Avance Real sigue siendo el que se certifica. */}
              <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs w-28 text-center">% Operativo</th>
              <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs w-32">% Avance Real</th>
              <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs w-32">Última act.</th>
              <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs w-48">Observaciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.map((it, idx) => {
              // Fix BQ (2026-09-08): incluye el filtro por origen, el de sección y
              // el de avance; los encabezados de sección sin hijos visibles se ocultan.
              if (!idxVisibles.has(idx)) return null;
              if (it.is_group) {
                return (
                  <tr key={idx} className="bg-[#EBF3FB]" data-testid={`obra-item-group-${idx}`}>
                    <td className="px-3 py-2 font-bold text-[#2E6DA4] text-xs">{it.numero || ""}</td>
                    {/* Fix BP (2026-09-08): 8 columnas con la de % Operativo. */}
                    <td className="px-3 py-2 font-bold text-[#2E6DA4]" colSpan={7}>{it.descripcion}</td>
                  </tr>
                );
              }
              const info = origenInfo(it, adicionalesIds);
              return (
                <tr key={idx} className="hover:bg-gray-50/60" data-testid={`obra-item-row-${idx}`}>
                  <td className="px-3 py-2 text-gray-500 text-xs font-mono">{it.numero || "—"}</td>
                  <td className="px-3 py-2 text-gray-800">
                    <div className="flex items-start gap-2">
                      <span className="flex-1">{it.descripcion}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${info.badgeCls}`} data-testid={`obra-item-badge-${idx}`}>
                        {info.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{it.unidad}</td>
                  {/* Fix BN (2026-09-08): toLocaleString sin opciones mostraba hasta 3
                      decimales según el valor; ahora es siempre 2, como el resto. */}
                  <td className="px-3 py-2 text-right text-gray-700 tabular-nums">{formatCantidad(it.cantidad)}</td>
                  {/* Fix BP (2026-09-08): promedio de las tareas del ítem ponderado por
                      su fondo de M.O. Un guión significa que el ítem todavía no tiene
                      tareas generadas, no que el avance sea cero. */}
                  <td className="px-3 py-2 text-center" data-testid={`obra-operativo-${idx}`}>
                    {operativo[it.item_uid] === undefined
                      ? <span className="text-gray-300">—</span>
                      : <span className={`inline-block rounded px-2 py-0.5 text-xs font-bold ${colorAvance(operativo[it.item_uid])}`}>{operativo[it.item_uid]}%</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number" min={0} max={100} value={it.avance_real_pct ?? 0}
                        onChange={e => setField(idx, "avance_real_pct", e.target.value)}
                        onBlur={() => save(idx)}
                        disabled={locked}
                        className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right focus:ring-2 focus:ring-[#EA580C]/20 focus:border-[#EA580C] disabled:bg-gray-100 disabled:text-gray-400"
                        data-testid={`obra-avance-input-${idx}`}
                      />
                      <span className="text-xs text-gray-400">%</span>
                      {savingIdx === idx && <Loader2 size={13} className="animate-spin text-[#EA580C]" />}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {it.avance_real_updated_at ? (
                      <div><div>{formatDate(it.avance_real_updated_at)}</div><div className="text-[10px]">{it.avance_real_updated_by_name}</div></div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      value={it.avance_real_observaciones || ""}
                      onChange={e => setField(idx, "avance_real_observaciones", e.target.value)}
                      onBlur={() => save(idx)}
                      disabled={locked}
                      placeholder="Nota..."
                      className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-[#EA580C]/20 focus:border-[#EA580C] disabled:bg-gray-100"
                      data-testid={`obra-obs-input-${idx}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Subtotales por origen + gran total — oculto para Residente de Obra (nunca ve montos) */}
          {!isResidente && (
            <tfoot className="border-t-2 border-gray-200" data-testid="obra-subtotales">
              {Object.entries(subtotales).map(([label, monto]) => (
                <tr key={label} className="bg-gray-50/60 text-xs">
                  <td colSpan={3} className="px-3 py-2 text-right text-gray-500 font-medium">Subtotal {label}</td>
                  <td colSpan={5} className="px-3 py-2 font-semibold text-gray-700 tabular-nums">{formatPYG(monto)}</td>
                </tr>
              ))}
              <tr className="bg-[#FFF7ED] text-sm">
                <td colSpan={3} className="px-3 py-2.5 text-right text-gray-700 font-bold">Gran Total (neto)</td>
                <td colSpan={5} className="px-3 py-2.5 font-bold text-[#C2410C] tabular-nums" data-testid="obra-gran-total">{formatPYG(granTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}

      {/* Envío a certificación (Residente) */}
      {isResidente && !locked && (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm space-y-3" data-testid="obra-enviar-cert-block">
          <p className="text-sm font-semibold text-gray-700">Enviar avance para certificación</p>
          <p className="text-xs text-gray-400">Los % que modifiques arriba no se aplican todavía — se envían como una nueva certificación y quedan a la espera de aprobación del Gerente Técnico o Administrador.</p>
          <textarea
            value={obsEnvio} onChange={e => setObsEnvio(e.target.value)}
            placeholder="Observaciones para quien va a revisar (opcional)"
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#EA580C]/20 focus:border-[#EA580C]"
            data-testid="obra-enviar-cert-observaciones"
          />
          <button
            onClick={enviarCertificacion}
            disabled={enviando}
            className="flex items-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="obra-enviar-cert-btn"
          >
            {enviando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Enviar avance para certificación
          </button>
        </div>
      )}

      {/* Total ponderado */}
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm" data-testid="obra-avance-ponderado">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700">Avance Real Ponderado de la Obra</span>
          <span className="text-lg font-bold text-[#EA580C]">{ponderado.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div className="h-2.5 rounded-full bg-[#EA580C] transition-all" style={{ width: `${Math.min(100, ponderado)}%` }} />
        </div>
        <p className="text-xs text-gray-400 mt-2">Ponderado por el monto de cada ítem sobre el total de la obra.</p>
      </div>
    </div>
  );
}

// CEN 2.0 FASE 3 PARTE A — widget de marcas cotizadas, editable por el coordinador
// desde el módulo Obra sin exponer el resto del edit completo del presupuesto.
function MarcasCotizadasCard({ presupuestoId, canEdit }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!presupuestoId) return;
    axios.get(`${API}/budgets/${presupuestoId}/items-para-pedido`, opts)
      .then(({ data }) => { setData(data); setDraft(data.marcas_cotizadas || ""); })
      .catch(() => {});
  }, [presupuestoId]);
  useEffect(load, [load]);

  if (!presupuestoId) return null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/budgets/${presupuestoId}/marcas-cotizadas`, { marcas_cotizadas: draft }, opts);
      toast.success("Marcas cotizadas actualizadas");
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const split = data?.marcas_cotizadas_split;
  const hasSplit = split && Object.values(split).some(v => (v || "").trim());

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm" data-testid="obra-marcas-cotizadas-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5"><Tag size={13} className="text-[#EA580C]" /> Marcas Cotizadas</p>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} className="text-xs text-[#EA580C] hover:text-[#C2410C] font-semibold flex items-center gap-1" data-testid="obra-marcas-edit-btn">
            <Pencil size={12} /> Editar
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3}
            className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm resize-none" data-testid="obra-marcas-textarea" />
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-60" data-testid="obra-marcas-save-btn">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar
            </button>
            <button onClick={() => { setEditing(false); setDraft(data?.marcas_cotizadas || ""); }} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5">Cancelar</button>
          </div>
        </div>
      ) : hasSplit ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-600" data-testid="obra-marcas-split-view">
            {Object.entries(split).filter(([, v]) => (v || "").trim()).map(([k, v]) => (
              <span key={k}><strong className="capitalize">{k}:</strong> {v}</span>
            ))}
          </div>
          {(data?.marcas_cotizadas || "").trim() && (
            <p className="text-xs text-gray-500 border-t border-gray-100 pt-2" data-testid="obra-marcas-legacy-note">{data.marcas_cotizadas}</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-600" data-testid="obra-marcas-legacy-view">{data?.marcas_cotizadas || "Sin marcas cotizadas registradas."}</p>
      )}
    </div>
  );
}

// Fix CN (2026-09-13): "acá debe figurar el fondo total disponible para gastos de MO y
// MATERIALES, si figura desglosado mejor" (Edson, 13/09). El fondo sale del costeo del
// presupuesto de la obra (`GET /api/obras/{id}/fondos`).
function FondosObraCard({ obraId }) {
  const [fondos, setFondos] = useState(null);

  useEffect(() => {
    if (!obraId) return;
    axios.get(`${API}/obras/${obraId}/fondos`, opts)
      .then(({ data }) => setFondos(data))
      .catch(() => setFondos(null));
  }, [obraId]);

  if (!fondos) return null;
  return (
    <div className="mt-4 pt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="obra-fondos">
      {(fondos.rubros || []).map(r => (
        <div key={r.rubro} className="bg-gray-50/70 rounded-xl px-3 py-2" data-testid={`obra-fondo-${r.rubro}`}>
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Fondo {r.label}</p>
          <p className="text-base font-bold text-gray-900">{formatPYG(r.fondo)}</p>
          <p className="text-[11px] text-gray-500">
            Utilizado {formatPYG(r.utilizado)} ({r.pct_usado}%) · Disponible{" "}
            <span className={r.disponible >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
              {formatPYG(r.disponible)}
            </span>
          </p>
        </div>
      ))}
    </div>
  );
}

// CEN 2.0 FASE 3 PARTE B — consumo por ítem del presupuesto, con semáforo y filtro por nivel.
function ConsumoPorItemCard({ presupuestoId, vistaCliente }) {
  const [niveles, setNiveles] = useState([]);
  const [nivelFiltro, setNivelFiltro] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totales, setTotales] = useState(null);   // Fix CN (2026-09-13)

  useEffect(() => {
    if (!presupuestoId) return;
    axios.get(`${API}/budgets/${presupuestoId}/items-para-pedido`, opts)
      .then(({ data }) => setNiveles(data.niveles || [])).catch(() => {});
  }, [presupuestoId]);

  useEffect(() => {
    if (!presupuestoId) return;
    setLoading(true);
    // Fix W (2026-09-02): consumo por MATERIAL del listado desglosado, no por
    // ítem del presupuesto (para una boca, comparar los metros de cable
    // comprados contra la cantidad de bocas no significa nada).
    axios.get(`${API}/budgets/${presupuestoId}/consumo-materiales`, { ...opts, params: nivelFiltro ? { nivel_id: nivelFiltro } : {} })
      .then(({ data }) => {
        setRows(data?.materiales || []);
        // Fix CN (2026-09-13): totales en Gs (el Residente no los recibe).
        setTotales(data?.con_montos
          ? { presupuestado: data.monto_presupuestado_total || 0, comprado: data.monto_comprado_total || 0 }
          : null);
      })
      .catch(() => toast.error("No se pudo cargar el consumo por material"))
      .finally(() => setLoading(false));
  }, [presupuestoId, nivelFiltro]);

  if (!presupuestoId) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm" data-testid="obra-consumo-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5"><Layers size={13} className="text-[#EA580C]" /> Consumo por Material del Presupuesto</p>
        {niveles.length > 0 && (
          <select value={nivelFiltro} onChange={e => setNivelFiltro(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white" data-testid="obra-consumo-nivel-filter">
            <option value="">Todos los niveles</option>
            {niveles.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
        )}
      </div>
      {/* Fix CN (2026-09-13): totales en dinero de lo presupuestado y lo comprado. */}
      {totales && !vistaCliente && (
        <div className="flex flex-wrap gap-4 mb-3 text-xs" data-testid="obra-consumo-totales">
          <span className="text-gray-500">Total presupuestado: <strong className="text-gray-800">{formatPYG(totales.presupuestado)}</strong></span>
          <span className="text-gray-500">Total comprado (sin IVA): <strong className="text-gray-800">{formatPYG(totales.comprado)}</strong></span>
          <span className={totales.presupuestado - totales.comprado >= 0 ? "text-emerald-600" : "text-red-600"}>
            Diferencia: <strong>{formatPYG(totales.presupuestado - totales.comprado)}</strong>
          </span>
        </div>
      )}
      {loading ? <Skeleton className="h-24 w-full rounded-xl" /> : rows.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-4 text-center">Sin materiales en el listado del presupuesto todavía. Los pedidos nuevos se imputan desde la columna "Ítem del Pto"; las compras anteriores, desde la pestaña Actualización 2.0.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 uppercase text-[10px] border-b border-gray-100">
                <th className="text-left py-1.5 pr-3 max-lg:min-w-[180px]">Material</th>
                <th className="text-right py-1.5 pr-3">Presup.</th>
                {/* Fix CN (2026-09-13): precio unitario del costeo y el total en Gs.
                    Fix IF (2026-10-29): en la vista del cliente solo cantidades. */}
                {!vistaCliente && <th className="text-right py-1.5 pr-3">P. Unit.</th>}
                {!vistaCliente && <th className="text-right py-1.5 pr-3">Total Presup.</th>}
                <th className="text-right py-1.5 pr-3">Solicitado</th>
                <th className="text-right py-1.5 pr-3">Comprado</th>
                {!vistaCliente && <th className="text-right py-1.5 pr-3">Total Comprado</th>}
                <th className="text-center py-1.5">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r, i) => {
                const est = CONSUMO_ESTADO[r.estado] || CONSUMO_ESTADO.ok;
                return (
                  <tr key={i} data-testid={`obra-consumo-row-${i}`}>
                    <td className="py-1.5 pr-3 font-medium text-gray-800" title={(r.de_items || []).join(" · ")}>
                      {r.descripcion} <span className="text-gray-400">({r.unidad})</span>
                      {r.tipo === "item" ? <span className="ml-1 text-[9px] text-gray-400 uppercase">ítem</span> : null}
                      {/* Fix CX (2026-09-14): sale del desglose de un alimentador, así que su
                          precio unitario está prorrateado por sección y no es un precio cotizado. */}
                      {r.origen === "alimentador" ? (
                        <span className="ml-1 text-[9px] text-amber-600 uppercase" title="Sale del desglose de un alimentador · el precio unitario está prorrateado por sección" data-testid={`obra-consumo-desglosado-${i}`}>desglosado</span>
                      ) : null}
                    </td>
                    {/* Fix BN (2026-09-08): antes se interpolaba el float crudo y la
                        columna mezclaba 7218.0054 con 350 y con 2. El valor no cambia:
                        cambia cómo se lo muestra. */}
                    <td className="py-1.5 pr-3 text-right tabular-nums" title={String(r.presupuestado)}>{formatCantidad(r.presupuestado)}</td>
                    {!vistaCliente && <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500">{r.precio_unit === undefined ? "—" : formatPYG(r.precio_unit)}</td>}
                    {!vistaCliente && <td className="py-1.5 pr-3 text-right tabular-nums">{r.monto_presupuestado === undefined ? "—" : formatPYG(r.monto_presupuestado)}</td>}
                    <td className="py-1.5 pr-3 text-right tabular-nums">{formatCantidad(r.solicitado)}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums font-semibold">{formatCantidad(r.comprado)}</td>
                    {!vistaCliente && (
                      <td className="py-1.5 pr-3 text-right tabular-nums font-semibold" title={r.precio_unit_comprado ? `P. unit. comprado: ${formatPYG(r.precio_unit_comprado)}` : ""}>
                        {r.monto_comprado === undefined ? "—" : formatPYG(r.monto_comprado)}
                      </td>
                    )}
                    <td className="py-1.5 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${est.cls}`}>{est.label} · {formatCantidad(r.pct_comprado)}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Fix BP (2026-09-08): matriz de avance operativo ─────────────────────────
// Filas = tareas, columnas = niveles, como la planilla que Edson venía llevando
// aparte. Los cortes de color son los mismos de su formato condicional. Las
// clases van escritas enteras y literales: Tailwind solo compila lo que encuentra
// escrito, así que armarlas concatenando las dejaría sin estilo en producción.
function colorAvance(pct) {
  const p = Number(pct) || 0;
  if (p >= 100) return "bg-emerald-300 text-emerald-900";
  if (p >= 80) return "bg-cyan-200 text-cyan-900";
  if (p >= 51) return "bg-yellow-200 text-yellow-900";
  if (p >= 21) return "bg-orange-300 text-orange-900";
  if (p >= 6) return "bg-fuchsia-300 text-fuchsia-900";
  if (p >= 1) return "bg-red-400 text-white";
  return "bg-white text-gray-400";
}

function MatrizAvanceCard({ obraId, onCambio }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/obras/${obraId}/avance-operativo`, opts)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Error cargando el avance operativo"))
      .finally(() => setLoading(false));
  }, [obraId]);
  useEffect(load, [load]);

  const guardarCelda = async (tarea, nivelId, valor) => {
    const pct = Math.max(0, Math.min(100, Number(valor) || 0));
    const actual = data?.filas?.find(f => f.tarea === tarea)?.celdas?.[nivelId];
    if (actual && Math.abs(actual.pct - pct) < 0.05) return;
    const clave = `${tarea}||${nivelId}`;
    setGuardando(clave);
    try {
      await axios.put(`${API}/obras/${obraId}/avance-operativo/celda`,
        { tarea_nombre: tarea, nivel_id: nivelId, pct }, opts);
      setData(prev => ({
        ...prev,
        filas: prev.filas.map(f => f.tarea !== tarea ? f
          : { ...f, celdas: { ...f.celdas, [nivelId]: { ...f.celdas[nivelId], pct } } }),
      }));
      if (onCambio) onCambio();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar el avance");
      load();
    } finally { setGuardando(""); }
  };

  if (loading) return <Skeleton className="h-64 w-full rounded-2xl" />;
  if (!data || data.filas.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200/60 p-8 text-center shadow-sm" data-testid="obra-matriz-vacia">
        <ClipboardList size={36} className="mx-auto text-gray-200 mb-3" />
        <p className="text-gray-600 font-medium">Todavía no hay tareas para cargar avance</p>
        <p className="text-xs text-gray-400 mt-1">Generá las tareas desde la pestaña Tareas y volvé acá.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="obra-matriz-avance">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Avance operativo por tarea y nivel</p>
          <p className="text-[11px] text-gray-400">Se escribe el % en la celda. Alimenta la columna "% Operativo" de la vista Ítems.</p>
        </div>
        {/* Fix BU (2026-09-09): "Esta presentación tbn nos serviría para generar
            luego un informe en formato CEN para presentacion al cliente (…) donde
            el cliente pueda ver el avance operativo y el cronograma de tareas
            calendarizadas con el gantt" (Edson, 08/09). Se abre en una pestaña
            nueva, igual que el resto de los PDFs del sistema desde el Fix AQ:
            en el celular la descarga directa no abría nada. */}
        <button type="button"
          onClick={() => window.open(`${API}/obras/${obraId}/informe-cliente-pdf`, "_blank")}
          className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-lg px-3 py-1.5 text-xs font-semibold"
          data-testid="obra-informe-cliente-btn">
          <FileImage size={13} /> Informe para el cliente (PDF)
        </button>
      </div>
      <div className="overflow-auto max-h-[70vh]">
        <table className="text-xs border-collapse">
          <thead className="sticky top-0 z-20 [&_th]:sticky [&_th]:top-0 [&_th]:z-20 [&_th]:bg-gray-50">
            <tr className="text-gray-500 uppercase text-[10px]">
              <th className="px-3 py-2 text-left font-semibold left-0 !z-30 min-w-[240px] border-r border-gray-200" style={{ position: "sticky", left: 0 }}>Tarea</th>
              {data.niveles.map(n => (
                <th key={n.id || "sin"} className="px-2 py-2 text-center font-semibold min-w-[64px] whitespace-nowrap" title={n.label}>{n.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data.filas.map(f => (
              <tr key={f.tarea} data-testid={`obra-matriz-fila-${f.tarea}`}>
                <td className="px-3 py-1.5 font-medium text-gray-800 bg-white border-r border-gray-200 whitespace-nowrap" style={{ position: "sticky", left: 0 }} title={f.tarea}>{f.tarea}</td>
                {data.niveles.map(n => {
                  const c = f.celdas[n.id];
                  if (!c) return <td key={n.id || "sin"} className="px-1 py-1.5 text-center text-gray-200">·</td>;
                  const clave = `${f.tarea}||${n.id}`;
                  return (
                    <td key={n.id || "sin"} className="px-1 py-1.5 text-center">
                      <input
                        type="number" min={0} max={100} defaultValue={c.pct}
                        onBlur={e => guardarCelda(f.tarea, n.id, e.target.value)}
                        title={`${c.n} tarea(s) en esta celda`}
                        className={`w-14 rounded px-1 py-0.5 text-center font-semibold border border-transparent focus:border-[#EA580C] focus:bg-white ${colorAvance(c.pct)} ${guardando === clave ? "opacity-50" : ""}`}
                        data-testid={`obra-matriz-celda-${f.tarea}-${n.id}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── CENTRO DE COSTOS ───────────────────────────────────────────────────────
// CEN 2.0 FASE 5 CHECKPOINT A — comprado/consumido/en depósito REAL (movimientos
// de inventario: entradas a los almacenes de la obra vs consumo obrador->instalado).
function ConsumoRealAlmacenCard({ obraId, presupuestoId }) {
  const [niveles, setNiveles] = useState([]);
  const [nivelFiltro, setNivelFiltro] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!presupuestoId) return;
    axios.get(`${API}/budgets/${presupuestoId}/items-para-pedido`, opts).then(({ data }) => setNiveles(data.niveles || [])).catch(() => {});
  }, [presupuestoId]);

  useEffect(() => {
    if (!obraId) return;
    setLoading(true);
    axios.get(`${API}/obras/${obraId}/consumo-real`, { ...opts, params: nivelFiltro ? { nivel_id: nivelFiltro } : {} })
      .then(({ data }) => setRows(data.items || []))
      .catch(() => toast.error("Error cargando consumo real de almacenes"))
      .finally(() => setLoading(false));
  }, [obraId, nivelFiltro]);

  if (!obraId) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm" data-testid="obra-consumo-real-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5"><Warehouse size={13} className="text-[#2E6DA4]" /> Comprado vs Consumido (Almacenes)</p>
        {niveles.length > 0 && (
          <select value={nivelFiltro} onChange={e => setNivelFiltro(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white" data-testid="obra-consumo-real-nivel-filter">
            <option value="">Todos los niveles</option>
            {niveles.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
          </select>
        )}
      </div>
      {loading ? <Skeleton className="h-24 w-full rounded-xl" /> : rows.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-4 text-center">Sin movimientos de inventario registrados para esta obra todavía.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 uppercase text-[10px] border-b border-gray-100">
                <th className="text-left py-1.5 pr-3 max-lg:min-w-[180px]">Material</th>
                <th className="text-right py-1.5 pr-3">Comprado</th>
                <th className="text-right py-1.5 pr-3">Consumido{nivelFiltro ? " (nivel)" : ""}</th>
                <th className="text-right py-1.5">En Depósito</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r) => (
                <tr key={r.material_id} data-testid="obra-consumo-real-row">
                  <td className="py-1.5 pr-3 font-medium text-gray-800">{r.material_name} <span className="text-gray-400">({r.unit})</span></td>
                  {/* Fix BN (2026-09-08): mismo criterio que la tarjeta de arriba. */}
                  <td className="py-1.5 pr-3 text-right tabular-nums">{formatCantidad(r.comprado)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-semibold">{formatCantidad(r.consumido)}</td>
                  <td className="py-1.5 text-right tabular-nums text-emerald-600 font-semibold">{formatCantidad(r.en_deposito)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CentroCostosTab({ obraId, presupuestoId, canEditMarcas, obra }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Fix EC (2026-09-24): "en el centro de costos no está diferenciado entre contrato
  // principal y adicionales". Todos, Base o un adicional: cambian los totales, la lista
  // de compras y las tarjetas de abajo.
  const [origen, setOrigen] = useState("todos");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/obras/${obraId}/centro-costos`, opts)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Error cargando centro de costos"))
      .finally(() => setLoading(false));
  }, [obraId]);

  if (loading) return <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  const adicionalesIds = obra?.adicionales_ids || [];
  const elegido = origen === "todos" ? null : (data?.origenes || []).find(o => o.clave === origen);
  const k = elegido || data?.kpis || {};
  const compras = (data?.compras || []).filter(c => !elegido || (c.presupuesto_id || "") === elegido.presupuesto_id);
  const etiquetaDe = (pid) => ((data?.origenes || []).find(o => o.presupuesto_id === pid) || {}).label || "";
  const presupuestoTarjetas = (elegido && elegido.presupuesto_id) || presupuestoId;
  const kpis = [
    { label: "Presupuestado", value: k.presupuestado, icon: DollarSign, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Comprado", value: k.comprado, icon: ShoppingCart, color: "text-orange-600", bg: "bg-orange-50" },
    { label: "Pagado", value: k.pagado, icon: CreditCard, color: "text-red-600", bg: "bg-red-50" },
    { label: "Saldo", value: k.saldo, icon: Wallet, color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  return (
    <div className="space-y-5" data-testid="obra-centro-costos-tab">
      <ChipsOrigen adicionalesIds={adicionalesIds} valor={origen} onChange={setOrigen} testId="obra-centro-origen" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {kpis.map(s => (
          <div key={s.label} className={`${s.bg} rounded-2xl p-4`} data-testid={`obra-kpi-${s.label.toLowerCase()}`}>
            <s.icon size={17} className={`${s.color} mb-2`} />
            <p className="text-xs text-gray-500 mb-0.5">{s.label}</p>
            <p className={`text-lg font-bold ${s.color}`}>{formatPYG(s.value || 0)}</p>
          </div>
        ))}
      </div>

      {compras.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-8 text-center shadow-sm">
          <TrendingDown size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-600 font-medium">Sin compras ni gastos vinculados</p>
          <p className="text-xs text-gray-400 mt-1">{data?.message || "Vincular compras desde el módulo Compras seleccionando esta Obra"}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-200/60 bg-white" data-testid="obra-compras-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left">
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">N° OC</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">Proveedor</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">Factura</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">Estado</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs">Fecha</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs text-right">Total</th>
                <th className="px-3 py-3 font-semibold text-gray-500 uppercase tracking-wide text-xs text-center">Pago</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {compras.map((c, idx) => (
                <tr key={c.oc_id || idx} className="hover:bg-gray-50/60" data-testid={`obra-compra-row-${idx}`}>
                  <td className="px-3 py-2.5 font-mono text-xs text-gray-700">{c.number}</td>
                  <td className="px-3 py-2.5 text-gray-800">
                    {c.proveedor}
                    {adicionalesIds.length > 0 && etiquetaDe(c.presupuesto_id) && etiquetaDe(c.presupuesto_id) !== "Base" && (
                      <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700" data-testid={`obra-compra-origen-${idx}`}>
                        {etiquetaDe(c.presupuesto_id)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500 font-mono">{c.invoice_number || "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 capitalize">{(c.status || "").replace(/_/g, " ")}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{formatDate(c.fecha)}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-gray-900 tabular-nums">{formatPYG(c.total)}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.pagado ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`obra-compra-pago-badge-${idx}`}>
                      {c.pagado ? "Pagado" : "Pendiente"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CEN 2.0 FASE 3 — marcas cotizadas + consumo por ítem del presupuesto */}
      <MarcasCotizadasCard presupuestoId={presupuestoTarjetas} canEdit={canEditMarcas} />
      <ConsumoPorItemCard presupuestoId={presupuestoTarjetas} />
      <ConsumoRealAlmacenCard obraId={obraId} presupuestoId={presupuestoTarjetas} />
      {/* CEN 2.0 FASE 5 Checkpoint B — tres planillas + flujo de caja */}
      <PlanillasFinancierasCard obraId={obraId} />
      {/* Auditoría 2026-08-27, CAMBIO 3 — snapshot del cierre financiero (solo lectura) */}
      {obra?.cierre_financiero && <CierreFinancieroCard cierre={obra.cierre_financiero} />}
    </div>
  );
}

// Auditoría 2026-08-27, CAMBIO 3 — snapshot de cierre financiero. Solo lectura:
// se genera automáticamente al finalizar la obra (obras.py::update_obra) y sólo
// un Superadministrador puede regenerarlo, reabriendo la obra y finalizándola
// de nuevo.
function CierreFinancieroCard({ cierre }) {
  const rows = [
    { label: "Previsto — Materiales", value: cierre.previsto?.materiales },
    { label: "Previsto — Mano de Obra", value: cierre.previsto?.mano_obra },
    { label: "Previsto — Gastos Admin.", value: cierre.previsto?.gastos_admin },
    { label: "Previsto — Total", value: cierre.previsto?.total, bold: true },
    { label: "Real — Materiales", value: cierre.real?.materiales },
    { label: "Real — Mano de Obra", value: cierre.real?.mano_obra },
    { label: "Real — Gastos Admin.", value: cierre.real?.gastos_admin },
    { label: "Real — Total", value: cierre.real?.total, bold: true },
  ];
  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="cierre-financiero-card">
      <div className="px-5 py-4 border-b border-gray-100 bg-slate-50">
        <h3 className="font-bold text-gray-900">Cierre Financiero de la Obra</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Generado el {formatDate(cierre.generated_at)} por {cierre.generated_by || "Sistema"} — no editable
        </p>
      </div>
      <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {rows.map((r) => (
          <div key={r.label}>
            <p className="text-xs text-gray-400">{r.label}</p>
            <p className={`text-sm ${r.bold ? "font-bold text-gray-900" : "text-gray-700"}`}>{formatPYG(r.value || 0)}</p>
          </div>
        ))}
        <div>
          <p className="text-xs text-gray-400">Certificado / Cobrado</p>
          <p className="text-sm font-semibold text-[#2E6DA4]">{formatPYG(cierre.certificado_total || 0)} / {formatPYG(cierre.cobrado_total || 0)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">Margen Final</p>
          <p className="text-sm font-bold text-emerald-600" data-testid="cierre-margen-final">{(cierre.margen_final_pct || 0).toFixed(1)}%</p>
        </div>
        <div>
          <p className="text-xs text-gray-400">ROI Final</p>
          <p className="text-sm font-bold text-emerald-600" data-testid="cierre-roi-final">{(cierre.roi_final_pct || 0).toFixed(1)}%</p>
        </div>
      </div>
    </div>
  );
}

function PlanillasFinancierasCard({ obraId }) {
  const [costos, setCostos] = useState(null);
  const [flujo, setFlujo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/obras/${obraId}/costos`, opts).catch(() => null),
      axios.get(`${API}/obras/${obraId}/flujo-caja`, opts).catch(() => null),
    ]).then(([c, f]) => { setCostos(c?.data || null); setFlujo(f?.data || null); })
      .finally(() => setLoading(false));
  }, [obraId]);

  if (loading) return <Skeleton className="h-40 rounded-2xl" />;
  const RUBRO_LABEL = { materiales: "Materiales", mano_obra: "Mano de Obra", gastos_admin: "Gastos Admin." };

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm space-y-5" data-testid="planillas-financieras-card">
      <p className="text-sm font-bold text-gray-800">Planillas Financieras (previsto vs. utilizado)</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(costos?.planillas || []).map(p => {
          const pct = p.previsto > 0 ? Math.min(100, Math.round(p.utilizado / p.previsto * 100)) : 0;
          const over = p.utilizado > p.previsto;
          return (
            <div key={p.rubro} className="rounded-xl border border-gray-100 p-3.5" data-testid={`planilla-${p.rubro}`}>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{RUBRO_LABEL[p.rubro] || p.rubro}</p>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm font-bold text-gray-900 tabular-nums">{formatPYG(p.utilizado)}</span>
                <span className="text-[11px] text-gray-400">de {formatPYG(p.previsto)}</span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden mb-1.5">
                <div className={`h-full rounded-full ${over ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
              </div>
              <p className={`text-[11px] font-semibold ${p.disponible < 0 ? "text-red-600" : "text-gray-500"}`}>
                Disponible: {formatPYG(p.disponible)}
              </p>
            </div>
          );
        })}
      </div>

      {flujo?.meses?.length > 0 && (
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Flujo de Caja</p>
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-xs" data-testid="flujo-caja-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 font-bold text-gray-500">Mes</th>
                  <th className="text-right px-3 py-2 font-bold text-gray-500">Egr. Proy.</th>
                  <th className="text-right px-3 py-2 font-bold text-gray-500">Egr. Real</th>
                  <th className="text-right px-3 py-2 font-bold text-gray-500">Ing. Proy.</th>
                  <th className="text-right px-3 py-2 font-bold text-gray-500">Ing. Real</th>
                </tr>
              </thead>
              <tbody>
                {flujo.meses.map(m => (
                  <tr key={m.mes} className="border-t border-gray-50">
                    <td className="px-3 py-1.5 font-semibold text-gray-700">{m.mes}</td>
                    <td className="px-3 py-1.5 text-right text-gray-500 tabular-nums">{formatPYG(m.egresos_proyectados)}</td>
                    <td className="px-3 py-1.5 text-right text-red-600 tabular-nums">{formatPYG(m.egresos_reales)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-500 tabular-nums">{formatPYG(m.ingresos_proyectados)}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-600 tabular-nums">{formatPYG(m.ingresos_reales)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LÍNEA DE TIEMPO (history) ──────────────────────────────────────────────
const EVENT_META = {
  obra_creada:          { icon: PlusCircle, color: "text-emerald-600", bg: "bg-emerald-50", title: "Obra creada" },
  adicional_anexado:    { icon: Link2Off,  color: "text-orange-600",  bg: "bg-orange-50",  title: "Adicional anexado" },
  adicional_desvinculado:{ icon: Link2Off, color: "text-gray-500",    bg: "bg-gray-50",    title: "Adicional desvinculado" },
  status_cambiado:      { icon: FileText,  color: "text-blue-600",    bg: "bg-blue-50",    title: "Estado cambiado" },
  nombre_editado:       { icon: FileText,  color: "text-blue-600",    bg: "bg-blue-50",    title: "Nombre editado" },
  obra_eliminada:       { icon: Trash2,    color: "text-red-600",     bg: "bg-red-50",     title: "Obra eliminada" },
  obra_restaurada:      { icon: PlusCircle,color: "text-emerald-600", bg: "bg-emerald-50", title: "Obra restaurada" },
};

function TimelineModal({ obra, onClose }) {
  const history = [...(obra.history || [])].reverse();
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" data-testid="obra-timeline-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><HistoryIcon size={18} className="text-gray-500" /> Línea de Tiempo</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-3">
          {history.length === 0 ? <p className="text-sm text-gray-400 text-center py-6">Sin eventos.</p> :
            history.map((h, i) => {
              const meta = EVENT_META[h.event] || { icon: FileText, color: "text-gray-500", bg: "bg-gray-50", title: h.event };
              const Icon = meta.icon;
              return (
                <div key={i} className="flex gap-3" data-testid={`obra-timeline-event-${i}`}>
                  <div className={`p-2 rounded-xl ${meta.bg} ${meta.color} flex-shrink-0 h-fit`}><Icon size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm text-gray-900">{meta.title}</p>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">{formatDate(h.at)}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{h.comment || "—"}</p>
                    {h.monto ? <p className="text-xs font-bold text-orange-600 mt-0.5">{formatPYG(h.monto)}</p> : null}
                    {h.by_name ? <p className="text-[10px] text-gray-400 mt-0.5">por {h.by_name}</p> : null}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>, document.body);
}

// ─── MAIN ──────────────────────────────────────────────────────────────────
export default function ObraDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const isResidente = user?.role === RESIDENTE_ROLE;
  const canFullAccess = FULL_ACCESS_ROLES.includes(user?.role);
  const [obra, setObra] = useState(null);
  const [loading, setLoading] = useState(true);
  // Fix EE (2026-09-24): Tesorería → Tercerizados abre la obra en Presupuesto, Centro de
  // Costos o Pagos.
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["presupuesto", "centro", "pagos"].includes(t) ? t : "ejecucion";
  });
  const [showTimeline, setShowTimeline] = useState(false);
  const [vistaConsumo, setVistaConsumo] = useState("materiales");   // Fix CN (2026-09-13)
  // Fix IF (2026-10-29): "necesitamos tener la opción de visualización para el cliente" (CEN).
  // Solo cambia lo que se muestra en pantalla: oculta fondos y costos, no toca datos.
  const [vistaCliente, setVistaCliente] = useState(false);
  // Fix EC (2026-09-24): el consumo por material se mira por presupuesto (Base o cada adicional).
  const [origenConsumo, setOrigenConsumo] = useState("base");
  const [desvincularTarget, setDesvincularTarget] = useState(null);

  const load = useCallback(() => {
    // Fix V (2026-09-02): solo mostrar el Skeleton en la carga inicial. Un
    // refetch disparado por un tab hijo (ej. Activacion20Tab via onChanged)
    // no debe desmontar el árbol de tabs — eso le borraba su estado local
    // (el log de "Aplicar lo automático").
    setLoading(prev => (obra ? prev : true));
    axios.get(`${API}/obras/${id}`, opts)
      .then(({ data }) => setObra(data))
      .catch(() => { toast.error("Obra no encontrada"); navigate("/obra"); })
      .finally(() => setLoading(false));
  }, [id, navigate, obra]);
  useEffect(load, [id]);

  if (loading) return <div className="p-6 space-y-4"><Skeleton className="h-24 w-full rounded-2xl" /><Skeleton className="h-64 w-full rounded-2xl" /></div>;
  if (!obra) return null;
  const badge = STATUS_BADGE[obra.status] || { label: obra.status, cls: "bg-gray-100 text-gray-600" };
  const nAdic = (obra.adicionales_ids || []).length;

  const desvincular = async (budgetId) => {
    try {
      await axios.post(`${API}/obras/${id}/desvincular-adicional`, { budget_id: budgetId }, opts);
      toast.success("Adicional desvinculado");
      setDesvincularTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // Fix N (2026-08-31): "Consumo de Materiales" como pestaña propia, con el nombre
  // que le puso el cliente en el documento de Edson (Pto | Consumo Mat | Tareas |
  // Cert | Pagos | Informes | Archivos). Antes el consumo vivía dentro de Centro de
  // Costos, que el Residente no ve — o sea que justamente quien está en obra no
  // podía ver lo que se consumió. Las dos tarjetas ya existen y ya filtran montos
  // por su cuenta, así que solo cambian de lugar.
  const TABS = isResidente
    ? [
        ["ejecucion", "Ejecución en Campo", HardHat],
        ["presupuesto", "Presupuesto", Layers],
        ["consumo", "Consumo de Materiales", Boxes],
        ["tareas", "Tareas", ListChecks],
        ["flujo", "Flujo de Trabajo", ListTodo],
        ["pagos", "Pagos", HandCoins],
        ["informes", "Informes", FileImage],
        ["archivos", "Archivos", FolderOpen],
      ]
    : [
        ["ejecucion", "Ejecución en Campo", HardHat],
        ["centro", "Centro de Costos", DollarSign],
        ["presupuesto", "Presupuesto", Layers],
        ["consumo", "Consumo de Materiales", Boxes],
        ["tareas", "Tareas", ListChecks],
        ["flujo", "Flujo de Trabajo", ListTodo],
        ["certificaciones", "Certificaciones", Award],
        ["pagos", "Pagos", HandCoins],
        ["informes", "Informes", FileImage],
        ["archivos", "Archivos", FolderOpen],
        // Fix V (2026-09-02): solo para roles con acceso completo; el Residente no la ve.
        ["activacion", "Actualización 2.0", Boxes],
      ];

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="obra-detail-page">
      <button onClick={() => navigate("/obra")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 font-medium" data-testid="obra-back-btn">
        <ArrowLeft size={15} /> Volver a Obras
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-lg border border-gray-200">{obra.codigo}</span>
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${badge.cls}`}>{badge.label}</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight" data-testid="obra-detail-nombre">{obra.nombre}</h1>
            <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5"><Building2 size={14} />{obra.cliente_nombre}</p>
            <p className="text-xs text-gray-500 mt-1.5" data-testid="obra-presupuesto-base-line">
              Presupuesto Base: <span className="font-mono text-gray-700">{obra.presupuesto_base_number || "—"}</span>
              {nAdic > 0 && <span className="text-orange-600 font-semibold"> + {nAdic} adicional{nAdic !== 1 ? "es" : ""}</span>}
            </p>
          </div>
          <div className="text-right flex-shrink-0 space-y-2">
            {!isResidente && (
              <div>
                <p className="text-xs text-gray-400">Monto total (IVA incl.)</p>
                <p className="text-xl font-bold text-gray-900" data-testid="obra-detail-total">{formatPYG(obra.total)}</p>
              </div>
            )}
            <button onClick={() => setShowTimeline(true)} className="text-xs font-semibold text-[#EA580C] hover:text-[#C2410C] flex items-center gap-1 ml-auto" data-testid="obra-timeline-btn">
              <Clock size={13} /> Ver línea de tiempo
            </button>
            {/* Fix IF (2026-10-29): vista para mostrarle al cliente, sin fondos ni costos. */}
            {canFullAccess && (
              <button type="button" onClick={() => setVistaCliente(v => !v)} aria-pressed={vistaCliente}
                className={`text-xs font-semibold flex items-center gap-1 ml-auto rounded-lg px-2 py-1 border ${vistaCliente ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "text-[#2E6DA4] border-[#2E6DA4]/40 hover:bg-[#EBF3FB]"}`}
                data-testid="obra-vista-cliente-btn">
                <Eye size={13} /> {vistaCliente ? "Vista cliente activa · salir" : "Vista cliente"}
              </button>
            )}
          </div>
        </div>

        {/* Fix CN (2026-09-13): fondos disponibles por rubro, desde el presupuesto. */}
        {!isResidente && !vistaCliente && <FondosObraCard obraId={id} />}

        {/* Adicionales anexados (desvincular) */}
        {nAdic > 0 && canManage && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-2 items-center" data-testid="obra-adicionales-chips">
            <span className="text-xs text-gray-500">Adicionales:</span>
            {(obra.adicionales_ids || []).map((bid, i) => (
              <span key={bid} className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-full pl-2.5 pr-1 py-0.5 text-xs font-semibold">
                Adicional {i + 1}
                <button onClick={() => setDesvincularTarget(bid)} className="hover:bg-orange-200 rounded-full p-0.5" title="Desvincular" data-testid={`obra-desvincular-${bid}`}><X size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit overflow-x-auto">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${tab === key ? "bg-white text-[#EA580C] shadow-sm" : "text-gray-500 hover:text-gray-700"}`} data-testid={`obra-tab-${key}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "ejecucion" && <EjecucionTab obraId={id} adicionalesIds={obra.adicionales_ids || []} isResidente={isResidente} />}
      {tab === "centro" && <CentroCostosTab obraId={id} presupuestoId={obra.presupuesto_base_id} canEditMarcas={MARCAS_EDIT_ROLES.includes(user?.role)} obra={obra} />}
      {tab === "presupuesto" && <PresupuestoTab obra={obra} canFullAccess={canFullAccess} />}
      {/* Fix N (2026-08-31): las dos tarjetas que ya existían dentro de Centro de
          Costos, ahora también accesibles desde su pestaña propia. */}
      {tab === "consumo" && (
        <div className="space-y-5" data-testid="obra-consumo-tab">
          {/* Fix CN (2026-09-13): "podría ser una hoja aparte donde se cuente con la lista
              de cada material... y que desde ahí se pueda realizar las imputaciones"
              (Edson, 13/09). */}
          {!vistaCliente && (   /* Fix IF: en la vista del cliente no se ofrecen las compras */
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
            {[["materiales", "Por material"], ["compras", "Compras e imputación"]].map(([k, l]) => (
              <button key={k} onClick={() => setVistaConsumo(k)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${vistaConsumo === k ? "bg-white text-[#EA580C] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                data-testid={`obra-consumo-vista-${k}`}>{l}</button>
            ))}
          </div>
          )}
          {vistaConsumo === "compras" && !vistaCliente ? <ComprasImputacionTab obraId={id} /> : (
            <>
              <ChipsOrigen adicionalesIds={obra.adicionales_ids || []} valor={origenConsumo} onChange={setOrigenConsumo}
                conTodos={false} testId="obra-consumo-origen" />
              <ConsumoPorItemCard presupuestoId={origenConsumo === "base" ? obra.presupuesto_base_id : origenConsumo} vistaCliente={vistaCliente} />
              <ConsumoRealAlmacenCard obraId={id} presupuestoId={origenConsumo === "base" ? obra.presupuesto_base_id : origenConsumo} />
            </>
          )}
        </div>
      )}
      {tab === "tareas" && <TareasTab obraId={id} canFullAccess={canFullAccess} isResidente={isResidente} vistaCliente={vistaCliente} />}
      {/* Fix CM (2026-09-13): tareas del flujo de trabajo de esta obra. */}
      {tab === "flujo" && <TareasPanel modulo="obras" entidadTipo="obra" entidadId={id} entidadLabel={obra.nombre || obra.codigo || ""} />}
      {tab === "activacion" && canFullAccess && <Activacion20Tab obraId={id} onChanged={load} />}
      {tab === "certificaciones" && <CertificacionesTab obra={obra} canManage={canManage} />}
      {tab === "pagos" && <PagosTab obraId={id} canFullAccess={canFullAccess} isResidente={isResidente} />}
      {tab === "informes" && <InformesTab obraId={id} />}
      {tab === "archivos" && <ArchivosTab obraId={id} canFullAccess={canFullAccess} />}

      {showTimeline && <TimelineModal obra={obra} onClose={() => setShowTimeline(false)} />}

      {desvincularTarget && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDesvincularTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="obra-desvincular-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-amber-50 text-amber-600"><Link2Off size={20} /></div>
              <h2 className="text-lg font-bold text-gray-900">Desvincular Adicional</h2>
            </div>
            <p className="text-sm text-gray-600">¿Desvincular el adicional <strong>Adicional {(obra.adicionales_ids || []).indexOf(desvincularTarget) + 1}</strong> de esta obra? Se quitarán sus ítems y se restará su monto del total.</p>
            <div className="flex gap-3 pt-5">
              <button onClick={() => setDesvincularTarget(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
              <button onClick={() => desvincular(desvincularTarget)} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold" data-testid="obra-desvincular-confirm">Desvincular</button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
